// src/utils/settings.js
import {
  DynamoDBClient,
  GetItemCommand,
  PutItemCommand
} from '@aws-sdk/client-dynamodb';
import { marshall, unmarshall } from '@aws-sdk/util-dynamodb';
import { Auth } from 'aws-amplify';
import getAwsConfig from '../awsconfig';

// DynamoDB table name for settings
const TABLE_NAME = 'InventoryAppSettings';

// For local fallback during development or if there's an error connecting to DynamoDB.
const STORAGE_KEY = 'hg_inventory_settings';

// Simple cache to avoid loading settings too frequently
let cachedSettings = null;

/**
 * Create a DynamoDB client with proper AWS credentials
 * @returns {Promise<DynamoDBClient>} - The DynamoDB client
 */
async function makeClient() {
  const cfg = await getAwsConfig();
  return new DynamoDBClient(cfg);
}

/**
 * Get the current user's ID (Cognito sub)
 * @returns {Promise<string>} - The user ID or null if not authenticated
 */
async function getUserId() {
  try {
    const user = await Auth.currentAuthenticatedUser();
    return user.attributes.sub;
  } catch (error) {
    console.warn('Error getting user ID:', error);
    return null;
  }
}

/**
 * Load settings from DynamoDB
 * @returns {Promise<Object>} - The settings object
 */
async function load() {
  // Use cached settings if available
  if (cachedSettings) {
    return cachedSettings;
  }
  
  const userId = await getUserId();
  
  if (!userId) {
    console.warn('No user ID available, using localStorage fallback');
    return loadFromLocalStorage();
  }
  
  try {
    const db = await makeClient();
    const params = {
      TableName: TABLE_NAME,
      Key: marshall({
        userId,
        settingType: 'app-settings'
      })
    };
    
    const response = await db.send(new GetItemCommand(params));
    
    if (!response.Item) {
      return {}; // No settings found for this user
    }
    
    const item = unmarshall(response.Item);
    const settings = item.settings || {};
    
    // Cache the settings
    cachedSettings = settings;
    
    return settings;
  } catch (error) {
    console.error('Error loading settings from DynamoDB:', error);
    try {
      // Fallback to localStorage if DynamoDB fails
      return loadFromLocalStorage();
    } catch (localError) {
      console.error('Failed to load from localStorage as well:', localError);
      // Return default settings if all else fails
      return {
        units: {
          'PRODUCT-SEED': ['each', 'pack', 'box', 'gram', 'kg'],
          'PRODUCT-CLONE': ['each', 'plant', 'pack', 'box'],
          'SHIPPING_MATERIAL': ['each', 'roll', 'sheet', 'meter', 'foot', 'case', 'bundle', 'pallet']
        },
        locations: [],
        collections: [],
        notification: {
          recipient: 'admin@homegrown-genetics.com',
          enabled: true
        },
        automation: {
          seedsPerPack: 10,
          notifyOnLowStock: true,
          enableAutoProcessing: false,
          autoProcessingInterval: 60
        }
      };
    }
  }
}

/**
 * Save settings to DynamoDB
 * @param {Object} settings - The settings object to save
 * @returns {Promise<void>}
 */
async function save(settings) {
  // Update cache
  cachedSettings = settings;
  
  const userId = await getUserId();
  
  if (!userId) {
    console.warn('No user ID available, using localStorage fallback');
    saveToLocalStorage(settings);
    return;
  }
  
  try {
    const db = await makeClient();
    const params = {
      TableName: TABLE_NAME,
      Item: marshall({
        userId,
        settingType: 'app-settings',
        settings,
        lastUpdated: new Date().toISOString()
      })
    };
    
    await db.send(new PutItemCommand(params));
    
    // Also update localStorage as a backup
    saveToLocalStorage(settings);
  } catch (error) {
    console.error('Error saving settings to DynamoDB:', error);
    // Fallback to localStorage if DynamoDB fails
    saveToLocalStorage(settings);
    throw error; // Re-throw to allow callers to handle the error
  }
}

/**
 * Load settings from localStorage (fallback)
 * @returns {Object} - The settings object
 */
function loadFromLocalStorage() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
  } catch {
    return {};
  }
}

/**
 * Save settings to localStorage (fallback)
 * @param {Object} settings - The settings object to save
 */
function saveToLocalStorage(settings) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
}

/**
 * Read the entire settings object from DynamoDB.
 * @returns {Promise<Object>} - The settings object
 */
export async function getSettings() {
  return await load();
}

/**
 * Merge in only the top‐level keys in `updates`, then persist to DynamoDB.
 * @param {Object} updates - The updates to apply
 * @returns {Promise<Object>} - The merged settings object
 */
export async function setSettings(updates) {
  const current = await load();
  const merged = { ...current, ...updates };
  await save(merged);
  return merged;
}

/**
 * Clear the settings cache
 */
export function clearSettingsCache() {
  cachedSettings = null;
}
